<template>
  <TableComp
    v-if="sites"
    :name="$t('global.sites')"
    :headers="headers"
    :status="sites.status"
    :pagination="sites.pagination"
    :sort="sites.sort"
    :number-of-items="sites.list.length"
    empty-image="sites"
    :empty-bold-message="$t('global.no-sites-bold-message')"
    :empty-message="$t('global.no-sites-message')"
    v-on:update:list="updateList"
  >
    <!-- desktop table layout -->
    <template #desktop>
      <tr v-for="site in sites.list" v-bind:key="site.id">
        <!-- site domain column -->
        <td>
          <div class="d-inline-flex">
            <span class="mr-2">
              <router-link v-if="site.isStable" :to="getRouterLink(site)">
                {{ site.domain }}
              </router-link>
              <span v-else>{{ site.domain }}</span>
            </span>
            <router-link v-if="site.isStable" :to="getOptionsLink(site)">
              <Icon :size="14" icon="edit" color="gamma"
            /></router-link>
          </div>
        </td>

        <!-- temp domain column -->
        <td class="text-center">
          <a v-if="site.isStable" :href="`https://${site.tempDomain}`" target="_blank">
            {{ site.tempDomain }}
          </a>
          <span v-else>{{ site.tempDomain }}</span>
        </td>

        <!-- dev envs column -->
        <td class="text-center">
          <DevEnvironmentsDropDown
            v-if="canAddEnvironment(site)"
            :site="site"
            :disabled="!site.isStable"
          />
        </td>

        <!-- ip address column -->
        <td class="text-center">
          <div v-if="sites.status.isLoading" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="sites.status.isErrored" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else-if="site.ip">
            <span class="mr-2">{{ site.ip }}</span>
            <CopyToClipboard :text="site.ip" />
          </span>
        </td>

        <!-- three dots menu column -->
        <td class="text-end actions">
          <div v-if="site.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(site, plan)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="iconSize" />
            </div>
            <div class="text-capitalize">{{ site.state }}...</div>
          </div>
        </td>
      </tr>
    </template>

    <!-- mobile table layout -->
    <template #mobile>
      <TableMobileRow v-for="site in sites.list" v-bind:key="site.id">
        <template #header>
          <div class="d-flex align-items-center">
            <!-- site domain on the left -->
            <div class="flex-fill">
              <div class="d-inline-flex align-items-center">
                <span class="mr-2">
                  <router-link v-if="site.isStable" :to="getRouterLink(site)">
                    {{ site.domain }}
                  </router-link>
                  <span v-else>{{ site.domain }}</span>
                </span>
                <router-link v-if="site.isStable" :to="getOptionsLink(site)">
                  <Icon :size="14" icon="edit" color="gamma"
                /></router-link>
              </div>
            </div>

            <!-- three dot menu on the left -->
            <div>
              <div v-if="site.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(site, plan)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <LoadingState :size="iconSize" />
                </div>
                <div class="text-capitalize">{{ site.state }}...</div>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- temporary site domain -->
          <div class="row">
            <div class="col">{{ $t("global.temp-domain") }}:</div>
            <div class="col">
              <a v-if="site.isStable" :href="`https://${site.tempDomain}`" target="_blank">
                {{ site.tempDomain }}
              </a>
              <span v-else>{{ site.tempDomain }}</span>
            </div>
          </div>

          <!-- site ip -->
          <div class="row">
            <div class="col">{{ $t("global.ip-address") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <div v-else-if="site.ip">
                <span class="mr-2">{{ site.ip }}</span>
                <CopyToClipboard :text="site.ip" />
              </div>
            </div>
          </div>

          <!-- site ip -->
          <div v-if="canAddEnvironment(site)" class="row">
            <div class="col" />
            <div class="col">
              <DevEnvironmentsDropDown :site="site" :disabled="!site.isStable" />
            </div>
          </div>

          <!-- site status -->
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <span v-else class="text-capitalize">{{ site.state }}</span>
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { canWrite } from "@/utils/useAuth";
import { computed } from "@vue/composition-api";
import constants from "@/utils/constants";
import ContactSupport from "@/components/smart/modals/ContactSupport";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import DevEnvironmentsDropDown from "@/components/display/DevEnvironmentsDropDown";
import DevSiteAddModal from "@/components/smart/modals/DevSiteAdd";
import FreeTrialPayWallModal from "@/components/smart/modals/FreeTrialPayWall";
import Icon from "@/components/display/icon/Icon";
import ItemErrorState from "@/components/display/ItemErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import { Package } from "leatherman-js/models";
import SiteDelete from "@/components/smart/modals/SiteDelete";
import SiteUpdateDomain from "@/components/smart/modals/SiteUpdateDomain";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";

export default {
  name: "SitesTable",
  props: {
    sites: Object,
    plan: Object,
    packages: Object,
  },
  components: {
    DevEnvironmentsDropDown,
    Icon,
    ItemErrorState,
    LoadingState,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
    CopyToClipboard,
  },
  setup(props, { root }) {
    // package of the current plan being viewed
    const pack = computed(() => Package.getById(props.packages.list, props.plan.package.id) || {});

    const canAddEnvironment = (site) =>
      canAddDevEnvironment(site) || canAddStagingEnvironment(site);

    // can add a dev site if user has not created the maximum amount yet
    const canAddDevEnvironment = (site) => {
      if (
        !(props.sites.status.isLoaded || props.sites.status.isUpdating) ||
        !props.packages.status.isLoaded
      )
        return false;
      return (
        canWrite("orders") &&
        pack.value.environmentType === "production" &&
        site.app.allowsDevSites &&
        site.devEnvironmentCount < pack.value.devEnvironments
      );
    };

    // can add a staging site if user has not created the maximum amount yet
    const canAddStagingEnvironment = (site) => {
      if (
        !(props.sites.status.isLoaded || props.sites.status.isUpdating) ||
        !props.packages.status.isLoaded
      )
        return false;
      return (
        canWrite("orders") &&
        pack.value.environmentType === "production" &&
        site.app.wordpressBased &&
        site.stagingEnvironmentCount < pack.value.stagingEnvironments
      );
    };

    // to delete a site, more than one site must remain on the plan (cannot have zero sites on a plan)
    // or site must not be a primary site (must be a secondary site)
    const canDeleteSite = (site) => {
      if (!props.sites.status.isLoaded || !props.packages.status.isLoaded) return false;
      return props.plan.cloudAccounts?.length > 1 || !!site.parentAccount;
    };

    const addDevSite = (plan, site) => {
      if (!plan.package.isFreeTrial) {
        store.dispatch("modals/open", {
          template: DevSiteAddModal,
          data: { plan, site, environmentType: "development" },
        });
      } else {
        store.dispatch("modals/open", {
          template: FreeTrialPayWallModal,
          data: {
            package: plan.package,
            app: site.app,
            plan,
          },
        });
      }
    };

    const addStagingSite = (plan) =>
      store.dispatch("modals/open", {
        template: DevSiteAddModal,
        data: { plan, site: plan.cloudAccount, environmentType: "staging" },
      });

    // table column headers for desktop version of site list table
    const headers = [
      new TableHeader(root.$t("global.domain"), null, "domain"),
      new TableHeader(root.$t("global.temp-domain"), "justify-content-center"),
      new TableHeader("", "justify-content-center"),
      new TableHeader(root.$t("global.ip-address"), "justify-content-center"),
      new TableHeader(),
    ];

    // menu items for three dots menu on the right
    const menuItems = (site, plan) => {
      const items = [
        // go to site dashboard
        new NavLink(root.$t("global.site-dashboard"), null, () => {
          root.$router.push({
            name: "plan-site-detail",
            params: { planId: props.plan.id, siteId: site.id },
          });
        }),
        // open support ticket modal
        new NavLink(root.$t("global.contact-support"), null, () => {
          store.dispatch("modals/open", {
            template: ContactSupport,
            data: { serviceId: props.plan.id, department: root.$t("global.support") },
          });
        }),
      ];

      if (site.canChangeDomain || !site.parentAccount) {
        items.push(
          new NavLink(
            root.$t("global.site-update-domain"),
            null,
            () => {
              store.dispatch("modals/open", {
                template: SiteUpdateDomain,
                data: { site },
              });
            },
            null
          )
        );
      }

      // go to staging site creation page
      if (canAddStagingEnvironment(site)) {
        items.push(
          new NavLink(
            root.$t("global.staging-add"),
            null,
            () => addStagingSite(plan, site),
            false,
            "default"
          )
        );
      }

      // go to dev site creation page
      if (canAddDevEnvironment(site)) {
        items.push(
          new NavLink(
            root.$t("global.dev-add"),
            null,
            () => addDevSite(plan, site),
            false,
            "default"
          )
        );
      }

      // delete site from list
      if (canDeleteSite(site)) {
        items.push(
          new NavLink(
            root.$t("global.delete-site"),
            null,
            () => {
              store.dispatch("modals/open", {
                template: SiteDelete,
                data: { site },
              });
            },
            null,
            "danger"
          )
        );
      }

      return items;
    };

    const getRouterLink = (site) => ({
      name: props.plan.isTShirt ? "plan-site-access" : "plan-site-performance",
      params: { siteId: site.id },
    });

    const getOptionsLink = (site) => ({
      name: "plan-site-domain-options",
      params: { siteId: site.id },
    });

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("sites/getList", {
        planId: props.plan.id,
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    const iconSize = constants.TABLE_ICON_SIZE;

    return {
      canAddEnvironment,
      headers,
      menuItems,
      updateList,
      getRouterLink,
      getOptionsLink,
      iconSize,
    };
  },
};
</script>
